'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { X, ListPlus, ChevronDown, Loader2, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import type {
  ShareRoomGuestInviteRow,
  ShareContentRoomMemberForSubmit,
} from '@/components/share-room/types'

type RoomRosterMember = {
  id: string
  name: string
  email: string
  role?: string
  company?: string
  avatar?: string
}

function nameToFirstLast(fullName: string): { firstName: string; lastName: string } {
  const t = fullName.trim()
  if (!t) return { firstName: '', lastName: '' }
  const i = t.indexOf(' ')
  if (i === -1) return { firstName: t, lastName: '' }
  return { firstName: t.slice(0, i).trim(), lastName: t.slice(i + 1).trim() }
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  return `${parts[0][0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
}

export type ShareRoomCustomerEmailInviteFormProps = {
  roomId: string
  currentUserId?: string
  contentName?: string
  accountName: string
  guestFirstName: string
  onGuestFirstNameChange: (value: string) => void
  guestLastName: string
  onGuestLastNameChange: (value: string) => void
  guestEmailField: string
  onGuestEmailFieldChange: (value: string) => void
  onAddGuestRow: () => void
  guestInviteRows: ShareRoomGuestInviteRow[]
  onRemoveGuest: (email: string) => void
  bulkInvitePasteOpen: boolean
  onBulkInvitePasteOpenChange: (open: boolean) => void
  bulkInvitePasteText: string
  onBulkInvitePasteTextChange: (value: string) => void
  onApplyBulkPaste: () => void
  message: string
  onMessageChange: (value: string) => void
  isSubmitting: boolean
  onSubmit: (existingRoomMembers: ShareContentRoomMemberForSubmit[]) => void | Promise<void>
  isPartnerExternalUser: boolean
  myCompanyAccount: { id: string; name: string } | null
  emailInviteAccountContext: 'my_company' | 'room_customer' | 'no_account'
  onEmailInviteAccountContextChange: (
    value: 'my_company' | 'room_customer' | 'no_account'
  ) => void
}

export function ShareRoomCustomerEmailInviteForm({
  roomId,
  currentUserId,
  contentName,
  accountName,
  guestFirstName,
  onGuestFirstNameChange,
  guestLastName,
  onGuestLastNameChange,
  guestEmailField,
  onGuestEmailFieldChange,
  onAddGuestRow,
  guestInviteRows,
  onRemoveGuest,
  bulkInvitePasteOpen,
  onBulkInvitePasteOpenChange,
  bulkInvitePasteText,
  onBulkInvitePasteTextChange,
  onApplyBulkPaste,
  message,
  onMessageChange,
  isSubmitting,
  onSubmit,
  isPartnerExternalUser,
  myCompanyAccount,
  emailInviteAccountContext,
  onEmailInviteAccountContextChange,
}: ShareRoomCustomerEmailInviteFormProps) {
  const [memberSearch, setMemberSearch] = useState('')
  const [memberResults, setMemberResults] = useState<RoomRosterMember[]>([])
  const [selectedMembers, setSelectedMembers] = useState<RoomRosterMember[]>([])
  const [memberPickerOpen, setMemberPickerOpen] = useState(false)
  const [inviteNewOpen, setInviteNewOpen] = useState(false)
  const [rosterLoading, setRosterLoading] = useState(false)
  const [rosterError, setRosterError] = useState<string | null>(null)

  const showContentRoster = Boolean(contentName)

  useEffect(() => {
    setMemberSearch('')
    setMemberResults([])
    setSelectedMembers([])
    setMemberPickerOpen(false)
    setInviteNewOpen(false)
  }, [roomId])

  useEffect(() => {
    if (!showContentRoster || !roomId) return
    if (!memberPickerOpen && memberSearch.trim().length === 0) return
    let cancelled = false
    const timer = window.setTimeout(() => {
      setRosterLoading(true)
      setRosterError(null)
      void (async () => {
      try {
        const params = new URLSearchParams({
          forContentShare: '1',
          limit: '12',
        })
        const q = memberSearch.trim()
        if (q) params.set('search', q)
        const res = await fetch(`/api/rooms/${roomId}/members?${params.toString()}`)
        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          throw new Error((err as { error?: string }).error || 'Could not load people in this room')
        }
        const data = (await res.json()) as RoomRosterMember[]
        if (cancelled) return
        const rows: RoomRosterMember[] = Array.isArray(data)
          ? data.map((m) => ({
              id: m.id,
              name: m.name || '',
              email: (m.email || '').trim(),
              role: m.role,
              company: m.company,
              avatar: m.avatar,
            }))
          : []
        setMemberResults(rows)
      } catch (e) {
        if (!cancelled) {
          setRosterError(e instanceof Error ? e.message : 'Could not load room members')
          setMemberResults([])
        }
      } finally {
        if (!cancelled) setRosterLoading(false)
      }
      })()
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [memberPickerOpen, memberSearch, roomId, showContentRoster])

  const visibleMemberResults = useMemo(() => {
    const selectedIds = new Set(selectedMembers.map((m) => m.id))
    return memberResults.filter((m) => m.id !== currentUserId && !selectedIds.has(m.id))
  }, [currentUserId, memberResults, selectedMembers])

  const selectMember = useCallback((member: RoomRosterMember) => {
    setSelectedMembers((prev) =>
      prev.some((m) => m.id === member.id) ? prev : [...prev, member]
    )
    setMemberSearch('')
    setMemberPickerOpen(false)
  }, [])

  const removeSelectedMember = useCallback((id: string) => {
    setSelectedMembers((prev) => prev.filter((m) => m.id !== id))
  }, [])

  const selectedMembersForSubmit = useMemo((): ShareContentRoomMemberForSubmit[] => {
    return selectedMembers.map((m) => {
      const { firstName, lastName } = nameToFirstLast(m.name)
      return {
        id: m.id,
        email: m.email,
        firstName: firstName || m.name,
        lastName,
      }
    })
  }, [selectedMembers])

  const summaryLine = useMemo(() => {
    if (!contentName) return null
    const nExisting = selectedMembers.length
    const nNew = guestInviteRows.length
    if (nExisting === 0 && nNew === 0) return null
    const parts: string[] = []
    if (nExisting > 0) {
      parts.push(
        `${nExisting} ${nExisting === 1 ? 'person' : 'people'} in this room will be notified`
      )
    }
    if (nNew > 0) {
      parts.push(
        `${nNew} new ${nNew === 1 ? 'person' : 'people'} will be invited to the room and this content`
      )
    }
    return parts.join('; ') + '.'
  }, [contentName, selectedMembers.length, guestInviteRows.length])

  const canSubmit =
    selectedMembers.length > 0 || guestInviteRows.length > 0
  const inviteNewSectionOpen =
    !showContentRoster || inviteNewOpen || guestInviteRows.length > 0

  const handlePrimaryClick = () => {
    void onSubmit(showContentRoster ? selectedMembersForSubmit : [])
  }

  if (isSubmitting) {
    return (
      <div className="flex min-h-[12rem] flex-shrink-0 items-center justify-center">
        <p>{contentName ? 'Sharing content...' : 'Sending invitations...'}</p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-lg space-y-8 py-1 text-left">
      {showContentRoster && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-slate-900">Recipients</h3>
          <p className="text-xs text-slate-600">
            Search people already in this room. If someone is not here yet, add them by email below.
          </p>
          <div className="relative">
            <div className="rounded-md border border-slate-200 bg-white p-2 shadow-sm focus-within:ring-2 focus-within:ring-slate-400">
              {selectedMembers.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {selectedMembers.map((member) => (
                    <span
                      key={member.id}
                      className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-green-500 px-2 py-1 text-xs text-white"
                    >
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white/20 text-[10px] font-medium">
                        {getInitials(member.name)}
                      </span>
                      <span className="truncate">{member.name}</span>
                      <button
                        type="button"
                        className="rounded-full p-0.5 hover:bg-white/20"
                        aria-label={`Remove ${member.name}`}
                        onClick={() => removeSelectedMember(member.id)}
                      >
                        <X className="h-3 w-3" aria-hidden />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                <Input
                  type="search"
                  value={memberSearch}
                  onChange={(e) => {
                    setMemberSearch(e.target.value)
                    setMemberPickerOpen(true)
                  }}
                  onFocus={() => setMemberPickerOpen(true)}
                  onBlur={() => {
                    window.setTimeout(() => setMemberPickerOpen(false), 120)
                  }}
                  placeholder={
                    selectedMembers.length > 0
                      ? 'Add more people'
                      : 'Search people in this room'
                  }
                  className="h-8 border-0 p-0 text-sm shadow-none focus-visible:ring-0"
                />
              </div>
            </div>

            {memberPickerOpen && (
              <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
                {rosterLoading && (
                  <div className="flex items-center gap-2 p-3 text-sm text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    Searching people…
                  </div>
                )}
                {!rosterLoading && rosterError && (
                  <div className="p-3 text-sm text-amber-800" role="alert">
                    {rosterError}
                  </div>
                )}
                {!rosterLoading && !rosterError && visibleMemberResults.length === 0 && (
                  <div className="p-3 text-sm text-slate-500">
                    {memberSearch.trim()
                      ? 'No room members match that search. Add them by email below.'
                      : 'Start typing to find people in this room.'}
                  </div>
                )}
                {!rosterLoading &&
                  !rosterError &&
                  visibleMemberResults.map((member) => (
                    <button
                      key={member.id}
                      type="button"
                      className="flex w-full min-w-0 items-center gap-3 px-3 py-2 text-left hover:bg-slate-50"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        selectMember(member)
                      }}
                    >
                      <Avatar className="h-8 w-8 shrink-0">
                        {member.avatar ? <AvatarImage src={member.avatar} alt="" /> : null}
                        <AvatarFallback className="bg-red-500 text-xs font-semibold text-white">
                          {getInitials(member.name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-slate-900">
                          {member.name}
                        </span>
                        <span className="block truncate text-xs text-slate-500">
                          {member.role ? `${member.role} • ` : ''}
                          {member.email}
                        </span>
                      </span>
                      {member.company && (
                        <span className="shrink-0 rounded-full bg-green-500 px-2 py-1 text-xs font-medium text-white">
                          {member.company}
                        </span>
                      )}
                    </button>
                  ))}
              </div>
            )}
          </div>
          {selectedMembers.length > 0 && (
            <p className="text-xs text-slate-500">
              {selectedMembers.length} {selectedMembers.length === 1 ? 'person' : 'people'} selected
            </p>
          )}
        </div>
      )}

      <div className="space-y-3">
        {showContentRoster && (
          <button
            type="button"
            className="inline-flex max-w-full items-center gap-2 rounded-md py-1.5 pl-0 pr-2 text-left text-sm font-medium text-slate-600 outline-none ring-offset-background transition-colors hover:bg-slate-50/80 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
            onClick={() => setInviteNewOpen((open) => !open)}
            aria-expanded={inviteNewSectionOpen}
          >
            <ListPlus className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
            <span>
              Invite someone new
              {guestInviteRows.length > 0 ? ` (${guestInviteRows.length} added)` : ''}
            </span>
            <ChevronDown
              className={cn(
                'h-4 w-4 shrink-0 text-slate-400 transition-transform',
                inviteNewSectionOpen && 'rotate-180'
              )}
              aria-hidden
            />
          </button>
        )}
        <div
          className={cn(
            'space-y-3',
            showContentRoster && !inviteNewSectionOpen && 'hidden'
          )}
        >
        <div className="grid gap-2 sm:grid-cols-2">
          <Input
            placeholder="First name"
            value={guestFirstName}
            onChange={(e) => onGuestFirstNameChange(e.target.value)}
          />
          <Input
            placeholder="Last name"
            value={guestLastName}
            onChange={(e) => onGuestLastNameChange(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            placeholder="Email address"
            type="email"
            autoComplete="off"
            value={guestEmailField}
            onChange={(e) => onGuestEmailFieldChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                onAddGuestRow()
              }
            }}
          />
          <Button type="button" variant="secondary" className="shrink-0" onClick={onAddGuestRow}>
            Add
          </Button>
        </div>

        {isPartnerExternalUser && myCompanyAccount ? (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-slate-600">
                New contacts will be saved under{' '}
                <span className="font-semibold">
                  {emailInviteAccountContext === 'my_company'
                    ? myCompanyAccount.name
                    : emailInviteAccountContext === 'room_customer'
                      ? accountName
                      : 'No account'}
                </span>
                .
              </span>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant={emailInviteAccountContext === 'my_company' ? 'default' : 'secondary'}
                  size="sm"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => onEmailInviteAccountContextChange('my_company')}
                >
                  My company
                </Button>
                <Button
                  type="button"
                  variant={emailInviteAccountContext === 'room_customer' ? 'default' : 'secondary'}
                  size="sm"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => onEmailInviteAccountContextChange('room_customer')}
                >
                  Customer
                </Button>
                <Button
                  type="button"
                  variant={emailInviteAccountContext === 'no_account' ? 'default' : 'secondary'}
                  size="sm"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => onEmailInviteAccountContextChange('no_account')}
                >
                  No account
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="border-t border-dashed border-slate-200/90 pt-2 !mt-2">
          <Collapsible open={bulkInvitePasteOpen} onOpenChange={onBulkInvitePasteOpenChange}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="inline-flex max-w-full items-center gap-2 rounded-md py-1.5 pl-0 pr-2 text-left text-[11px] leading-snug text-slate-500 outline-none ring-offset-background transition-colors hover:bg-slate-50/80 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
              >
                <ListPlus className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
                <span className="min-w-0">
                  <span className="text-slate-400">Optional — </span>
                  <span className="text-slate-600">paste a list to add several people</span>
                </span>
                <ChevronDown
                  className={cn(
                    'ml-0.5 h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform',
                    bulkInvitePasteOpen && 'rotate-180'
                  )}
                  aria-hidden
                />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2 border-l-2 border-slate-200 pl-3 pt-2">
              <Textarea
                placeholder="First last <name@company.com>; Jane Doe <jane@company.com>"
                value={bulkInvitePasteText}
                onChange={(e) => onBulkInvitePasteTextChange(e.target.value)}
                rows={4}
                className="resize-y font-mono text-xs"
              />
              <Button type="button" size="sm" variant="secondary" onClick={onApplyBulkPaste}>
                Add from pasted list
              </Button>
              <p className="text-[11px] text-slate-500">
                Separate entries with semicolons, commas, or new lines. Use semicolons between entries if a name
                contains a comma.
              </p>
            </CollapsibleContent>
          </Collapsible>
        </div>

        {guestInviteRows.length > 0 && (
          <div className="max-h-28 overflow-y-auto rounded-md border border-slate-200 bg-white p-2">
            <div className="flex flex-wrap gap-2">
              {guestInviteRows.map((r) => (
                <div
                  key={r.email}
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-800"
                  title={r.email}
                >
                  <span className="truncate">{r.email}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${r.email}`}
                    className="rounded-full p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-700"
                    onClick={() => onRemoveGuest(r.email)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        </div>

        <Textarea
          placeholder={
            contentName
              ? 'Optional note included in the notification email'
              : 'Message included in the invitation email (optional)'
          }
          value={message}
          onChange={(e) => onMessageChange(e.target.value)}
          rows={3}
          className="min-h-[80px] resize-y"
        />

        {summaryLine && (
          <p className="text-xs leading-snug text-slate-600" aria-live="polite">
            {summaryLine}
          </p>
        )}

        <Button className="w-full" onClick={handlePrimaryClick} disabled={!canSubmit || isSubmitting}>
          {contentName ? 'Share & notify' : 'Send invitation email'}
        </Button>
      </div>
    </div>
  )
}
